import { action, computed, makeObservable, reaction, observable } from 'mobx'
import type React from 'react'
import { createRef } from 'react'
import type { ListItem, VirtuosoHandle, VirtuosoProps } from 'react-virtuoso'

import type IUiStore from '@src/app/IUiStore'
import { DisposeBag } from '@src/lib/dispose'
import type Stepper from '@src/lib/stepper'

/**
 * We default this to 1, otherwise Virtuoso doesn't try to render the list for
 * the first time.
 *
 * @internal
 */
const defaultListHeight = 1

interface HighlighterSpacing {
  top?: number
  bottom?: number
  right?: number
  left?: number
}

export interface ListPadding {
  top: number
  bottom: number
}

/**
 * `select` mode will instruct the controller to select the item when it's
 * hovered, therefore the highlighter will move as we move the mouse between
 * items
 *
 * `independent` mode won't instruct the controller to select the item when it's
 * hovered, therefore the highlighter will stay in the currenly selected item,
 * and will only move if we click on the item itself. Note that the item should
 * have a hover state on its own
 */
type HoverMode = 'select' | 'independent'

/**
 * `select` mode will instruct the controller to select the item when it's
 * focused, therefore the highlighter will move as we focus items with the
 * `Tab` key
 *
 * `independent` mode won't instruct the controller to select the item when
 * it's focused, therefore the highlighter will stay in the currently selected
 * item, and it will only move if we click on the item itself. Note that the item
 * might need a focus state on its own
 */
type FocusMode = 'select' | 'independent'

export interface KeyboardListItem {
  id: string
}

interface KeyboardListControllerParams {
  preventResetOnDataChange?: boolean
}

export default abstract class KeyboardListController<T extends KeyboardListItem>
  implements IUiStore
{
  abstract data: readonly T[]
  abstract stepper: Stepper<T>
  abstract itemHoverMode: HoverMode
  abstract itemFocusMode: FocusMode
  abstract focusItemOnSelect: boolean

  /** @internal */

  readonly highlighterRef: React.MutableRefObject<HTMLDivElement | null> = createRef()

  /** @internal */

  readonly handleRef: React.MutableRefObject<VirtuosoHandle | null> = createRef()

  /**
   * The spacing of the highlighter div "within" the item it is highlighting.
   */
  readonly highlighterSpacing: HighlighterSpacing = {}

  /**
   * The max height of the viewport div.
   *
   * If specified, the viewport will be automatically resized but will not
   * exceed the specified height.
   */
  readonly maxViewportHeight: number | null = null

  /**
   * The height of the overall list.
   *
   * This is updated automatically as items are added or removed.
   */
  listHeight = defaultListHeight

  /**
   * The top and bottom padding of the overall list.
   *
   * This is implemented with a header and footer div at the beginning and end
   * of all items in the list, respectively.
   */
  readonly listPadding: ListPadding = { top: 0, bottom: 0 }

  /**
   * The fixed height of all items in the list, used for optimization.
   */
  readonly itemHeight: number | null = null

  protected readonly disposeBag = new DisposeBag()

  private highlighterAnimated = false
  private scrollerEl: HTMLElement | null = null

  disallowedShortcuts: Record<string, boolean> = {}

  constructor(
    protected params: KeyboardListControllerParams = {
      preventResetOnDataChange: false,
    },
  ) {
    makeObservable(this, {
      listHeight: observable,
      disallowedShortcuts: observable.ref,
      index: computed,
      pinToIndex: computed,
      initialTopMostItemIndex: computed,
      viewportHeight: computed,
      fixedItemHeight: computed,
      setScrollerElement: action.bound,
      handleListHeightChanged: action.bound,
      handleItemsRendered: action.bound,
      handleMouseMove: action.bound,
      handleItemMouseEnter: action.bound,
      updateHighlighter: action.bound,
    })

    this.disposeBag.add(
      reaction(
        () => this.index,
        (_, prevIndex) => {
          const animate =
            this.highlighterAnimated &&
            typeof this.index === 'number' &&
            typeof prevIndex === 'number' &&
            this.index >= 0 &&
            prevIndex >= 0

          if (this.focusItemOnSelect) {
            const itemEl = this.scrollerEl?.querySelector<HTMLDivElement>(
              `[data-item-index="${this.index}"]`,
            )

            const focusableElement =
              itemEl?.querySelector<HTMLButtonElement>('[data-menuitem]')

            if (focusableElement) {
              focusableElement.focus({ preventScroll: true })
            } else {
              itemEl?.focus({ preventScroll: true })
            }
          }

          this.updateHighlighter(animate)
        },
        {
          name: 'KeyboardListIndexChange',
          fireImmediately: true,
        },
      ),
    )

    if (!this.params.preventResetOnDataChange) {
      this.disposeBag.add(
        reaction(
          () => {
            // Safely check if data is defined before accessing it
            try {
              return this.data
            } catch (e) {
              // If there's an error accessing this.data, return an empty array
              return []
            }
          },
          (currentData) => {
            // Skip if data is empty or not yet initialized
            if (!currentData || currentData.length === 0) {
              return
            }

            // Safely access stepper
            try {
              const stepperItem = this.stepper?.item
              if (
                stepperItem &&
                !currentData.some((item) => item.id === stepperItem.id)
              ) {
                this.stepper?.start()
              }
            } catch (e) {
              console.error(e)
              // If stepper isn't initialized yet or has been disposed, silently fail
            }
          },
          {
            name: 'KeyboardListDataChange',
            fireImmediately: true,
          },
        ),
      )
    }
  }

  get index(): number | null {
    return this.stepper?.index ?? -1
  }

  get pinToIndex(): number | undefined {
    return undefined
  }

  get initialTopMostItemIndex(): VirtuosoProps<any, any>['initialTopMostItemIndex'] {
    return typeof this.pinToIndex === 'number' && this.pinToIndex >= 0
      ? { index: this.pinToIndex, align: 'center' }
      : 0
  }

  /** @internal */
  get viewportHeight(): number | undefined {
    return typeof this.maxViewportHeight === 'number'
      ? this.listHeight === defaultListHeight
        ? this.maxViewportHeight
        : Math.min(this.maxViewportHeight, this.listHeight)
      : undefined
  }

  /** @internal */
  get fixedItemHeight(): number | undefined {
    return typeof this.itemHeight === 'number' ? this.itemHeight : undefined
  }

  handleEnter?(item: T, index: number, event: KeyboardEvent): void

  handleEscape?(item: T, index: number, event: KeyboardEvent): void

  handleLoadMoreThresholdReached(): void {
    // do nothing
  }

  /** @internal */
  setScrollerElement(element: HTMLElement | Window | null) {
    if (element instanceof HTMLElement) {
      this.scrollerEl = element
    }
  }

  /** @internal */
  handleListHeightChanged(height: number) {
    this.listHeight = height
  }

  /** @internal */
  handleItemsRendered(_items: ListItem<T>[]) {
    this.updateHighlighter(false)
  }

  /** @internal */
  handleMouseMove() {
    this.highlighterAnimated = false
  }

  /** @internal */
  handleItemMouseEnter(item: T, index: number) {
    this.stepper.selectIndex(index, { skipBehavior: 'stay' })
  }

  /** @internal */
  updateHighlighter(animate: boolean) {
    requestAnimationFrame(() => {
      const virtuoso = this.handleRef.current
      const el = this.highlighterRef.current

      if (!virtuoso || !el) {
        return
      }
      if (animate) {
        el.style.transition = 'transform 256ms cubic-bezier(0.19, 1, 0.22, 1)'
      } else {
        el.style.transition = 'none'
      }
      const itemEl = this.scrollerEl?.querySelector(`[data-item-index="${this.index}"]`)

      if (
        this.scrollerEl &&
        itemEl &&
        typeof this.index === 'number' &&
        this.index >= 0
      ) {
        const { y: scrollerY } = this.scrollerEl.getBoundingClientRect()
        const { y: itemY, height: itemHeight } = itemEl.getBoundingClientRect()

        const scrollTop = this.scrollerEl.scrollTop
        const top = this.highlighterSpacing?.top ?? 0
        const bottom = this.highlighterSpacing?.bottom ?? 0
        const left = this.highlighterSpacing?.left ?? 10
        const right = this.highlighterSpacing?.right ?? 10
        const offset = Math.round(scrollTop + (itemY - scrollerY))

        el.style.height = `${itemHeight - bottom}px`
        el.style.top = `${top}px`
        el.style.left = `${left}px`
        el.style.right = `${right}px`
        el.style.transform = `translateY(${offset}px)`
        el.style.opacity = '1'
      } else {
        el.style.opacity = '0'
      }
      this.highlighterAnimated = true
    })
  }

  tearDown(): void {
    this.disposeBag.dispose()
  }
}
